import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { getAuth, getIdToken } from 'firebase/auth';
import axios from 'axios';

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL ||
  'https://backend-core-644060845776.us-central1.run.app';

// ─── Design tokens (Fintech dark: #0F172A bg, #4F46E5 indigo accent) ──────────
const S = {
  wrapper: {
    background: 'rgba(15, 23, 42, 0.65)',
    border: '1px solid rgba(79, 70, 229, 0.28)',
    borderRadius: '16px',
    padding: '24px',
    marginBottom: '40px',
    backdropFilter: 'blur(14px)',
    WebkitBackdropFilter: 'blur(14px)',
    boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '14px',
    marginBottom: '20px',
  },
  title: {
    fontSize: '1.15rem',
    fontWeight: '700',
    color: '#E2E8F0',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  queueBadge: {
    background: 'rgba(252, 211, 77, 0.12)',
    border: '1px solid rgba(252, 211, 77, 0.35)',
    color: '#FCD34D',
    borderRadius: '20px',
    padding: '4px 14px',
    fontSize: '0.78rem',
    fontWeight: '600',
    whiteSpace: 'nowrap',
    letterSpacing: '0.02em',
  },
  manualReviewBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    background: 'rgba(251, 191, 36, 0.13)',
    border: '1px solid rgba(251, 191, 36, 0.45)',
    color: '#FBBF24',
    borderRadius: '6px',
    padding: '3px 9px',
    fontSize: '0.73rem',
    fontWeight: '700',
    whiteSpace: 'nowrap',
    letterSpacing: '0.02em',
    marginBottom: '4px',
  },
  controlBar: {
    display: 'flex',
    gap: '12px',
    marginBottom: '16px',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  searchInput: {
    flex: '1',
    minWidth: '200px',
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(79, 70, 229, 0.3)',
    borderRadius: '8px',
    padding: '9px 14px',
    color: '#E2E8F0',
    fontSize: '0.875rem',
    outline: 'none',
    transition: 'border-color 0.2s',
  },
  categorySelect: {
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(79, 70, 229, 0.3)',
    borderRadius: '8px',
    padding: '9px 14px',
    color: '#E2E8F0',
    fontSize: '0.875rem',
    outline: 'none',
    cursor: 'pointer',
    transition: 'border-color 0.2s',
  },
  tableWrapper: {
    overflowX: 'auto',
    borderRadius: '10px',
    border: '1px solid rgba(79, 70, 229, 0.18)',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.85rem',
    minWidth: '640px',
  },
  th: {
    padding: '11px 16px',
    textAlign: 'left',
    background: 'rgba(79, 70, 229, 0.14)',
    color: '#A5B4FC',
    fontWeight: '600',
    fontSize: '0.72rem',
    textTransform: 'uppercase',
    letterSpacing: '0.07em',
    borderBottom: '1px solid rgba(79, 70, 229, 0.22)',
    whiteSpace: 'nowrap',
  },
  thAction: {
    padding: '11px 16px',
    textAlign: 'center',
    background: 'rgba(79, 70, 229, 0.14)',
    color: '#A5B4FC',
    fontWeight: '600',
    fontSize: '0.72rem',
    textTransform: 'uppercase',
    letterSpacing: '0.07em',
    borderBottom: '1px solid rgba(79, 70, 229, 0.22)',
    whiteSpace: 'nowrap',
    width: '80px',
  },
  td: {
    padding: '11px 16px',
    borderBottom: '1px solid rgba(255,255,255,0.045)',
    color: '#CBD5E1',
    whiteSpace: 'nowrap',
  },
  tdAction: {
    padding: '8px 16px',
    borderBottom: '1px solid rgba(255,255,255,0.045)',
    textAlign: 'center',
  },
  emptyState: {
    textAlign: 'center',
    padding: '48px 20px',
    color: '#475569',
    fontSize: '0.9rem',
  },
  downloadBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    background: 'rgba(79, 70, 229, 0.18)',
    border: '1px solid rgba(79, 70, 229, 0.4)',
    borderRadius: '7px',
    color: '#A5B4FC',
    padding: '5px 11px',
    fontSize: '0.78rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.18s',
    whiteSpace: 'nowrap',
  },
  downloadBtnLoading: {
    opacity: 0.55,
    cursor: 'not-allowed',
  },
  countLabel: {
    fontSize: '0.78rem',
    color: '#64748B',
    marginLeft: 'auto',
    whiteSpace: 'nowrap',
  },
  accountInput: {
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(79, 70, 229, 0.25)',
    borderRadius: '6px',
    padding: '5px 9px',
    color: '#E2E8F0',
    fontSize: '0.8rem',
    fontFamily: 'monospace',
    width: '100px',
    outline: 'none',
    transition: 'border-color 0.18s, box-shadow 0.18s',
    cursor: 'text',
  },
  accountInputSaving: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
};

// ─── Toast ─────────────────────────────────────────────────────────────────────

/**
 * Self-dismissing toast notification (bottom-right).
 * type: 'success' | 'error'
 */
const Toast = ({ message, type = 'success', onDone }) => {
  useEffect(() => {
    const t = setTimeout(onDone, 2500);
    return () => clearTimeout(t);
  }, [onDone]);

  const bg     = type === 'success' ? 'rgba(16, 185, 129, 0.92)' : 'rgba(239, 68, 68, 0.92)';
  const border = type === 'success' ? '1px solid rgba(52, 211, 153, 0.5)' : '1px solid rgba(252, 165, 165, 0.5)';
  const icon   = type === 'success' ? '✓' : '✕';

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        bottom: '28px',
        right: '28px',
        zIndex: 9999,
        background: bg,
        border,
        borderRadius: '10px',
        padding: '11px 18px',
        color: '#fff',
        fontSize: '0.875rem',
        fontWeight: '600',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
        animation: 'b2b-toast-in 0.28s cubic-bezier(0.34, 1.56, 0.64, 1) both',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
      }}
    >
      <span style={{ fontSize: '1rem' }}>{icon}</span>
      {message}
    </div>
  );
};

// ─── Account Cell ───────────────────────────────────────────────────────────────

/**
 * Editable cell for Cuenta Contable.
 * On blur or Enter, fires POST /api/b2b/vendor-mappings with the emisor as
 * vendor_keyword and the typed value as account_code.
 */
const AccountCell = ({ docId, emisor, initialCode, onSaved, onError }) => {
  const [value,   setValue]   = useState(initialCode || '');
  const [saving,  setSaving]  = useState(false);
  const committedRef = useRef(initialCode || '');

  useEffect(() => {
    if (initialCode && !committedRef.current) {
      setValue(initialCode);
      committedRef.current = initialCode;
    }
  }, [initialCode]);

  const persist = useCallback(async () => {
    const code = value.trim();
    if (!code || code === committedRef.current) return; // nothing to do
    if (!emisor) { onError('El emisor no está disponible'); return; }

    setSaving(true);
    try {
      const auth  = getAuth();
      const user  = auth.currentUser;
      if (!user) throw new Error('Not authenticated');
      const token = await getIdToken(user);

      const BACKEND_URL =
        process.env.REACT_APP_BACKEND_URL ||
        'https://backend-core-644060845776.us-central1.run.app';

      await axios.post(
        `${BACKEND_URL}/api/b2b/vendor-mappings`,
        { vendor_keyword: emisor.trim(), account_code: code },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      committedRef.current = code;
      onSaved(docId, code);
    } catch (err) {
      console.error('[B2BDataGrid] VendorMapping save failed:', err);
      onError('Error al guardar el mapeo');
    } finally {
      setSaving(false);
    }
  }, [value, emisor, docId, onSaved, onError]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') { e.target.blur(); }
    if (e.key === 'Escape') { setValue(committedRef.current); e.target.blur(); }
  }, []);

  return (
    <input
      id={`b2b-account-cell-${docId}`}
      type="text"
      value={value}
      disabled={saving}
      placeholder="Ej. 601-01"
      aria-label={`Código de cuenta para ${emisor || 'proveedor'}`}
      onChange={(e) => setValue(e.target.value)}
      onBlur={persist}
      onKeyDown={handleKeyDown}
      onFocus={(e) => {
        e.target.style.borderColor = 'rgba(99, 102, 241, 0.75)';
        e.target.style.boxShadow   = '0 0 0 3px rgba(99, 102, 241, 0.18)';
      }}
      onBlurCapture={(e) => {
        e.target.style.borderColor = 'rgba(79, 70, 229, 0.25)';
        e.target.style.boxShadow   = 'none';
      }}
      style={{
        ...S.accountInput,
        ...(saving ? S.accountInputSaving : {}),
      }}
      title={saving ? 'Guardando…' : 'Ingresa el código contable y presiona Enter o haz clic fuera'}
    />
  );
};

// ─── Helpers ───────────────────────────────────────────────────────────────────

/** Diccionario Bilingüe Ligero para Encabezados / Lightweight Bilingual Dictionary */
const formatHeader = (key) => {
  const dictionary = {
    monto_total: 'Monto Total / Amount',
    emisor: 'Emisor / Issuer',
    fecha: 'Fecha / Date',
    categoria: 'Categoría / Category',
    moneda: 'Moneda / Currency',
    impuestos: 'Impuestos / Taxes',
    subtotal: 'Subtotal',
    tax_lines: 'Tax Lines / Impuestos',
  };

  if (dictionary[key]) return dictionary[key];
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

/** Render monetary values using Costa Rican locale; fall through for other types. */
const formatCellValue = (key, value) => {
  if (value == null || value === '') return null; // rendered as — below

  // ── TAX LINES — map array of objects to a human-readable string ──────────
  // Without this guard, String([{...}]) produces "[object Object]".
  if (key === 'tax_lines') {
    if (!Array.isArray(value) || value.length === 0) return null; // renders as —
    return value
      .map((t) => {
        const rate = t.rate != null ? `${t.rate}%` : '?%';
        const type = t.type || 'Tax';
        return `${rate} (${type})`;
      })
      .join(', ');
  }

  if (key === 'monto_total') {
    const num = parseFloat(value);
    if (!isNaN(num)) {
      // moneda is resolved by the parent; here we just format the number
      return num.toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
  }
  return String(value);
};

// ─── Download button (self-contained, manages its own loading state) ───────────
const DownloadButton = ({ doc }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSingleDownload = useCallback(async () => {
    if (loading || !doc?.id) return;
    setError(null);
    setLoading(true);
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) throw new Error('Not authenticated');
      const token = await getIdToken(user);
      const url = `${BACKEND_URL}/api/b2b/documents/${doc.id}/download`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data?.download_url) {
        window.open(response.data.download_url, '_blank', 'noopener,noreferrer');
      } else if (doc.file_url) {
        window.open(doc.file_url, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      console.error('[B2BDataGrid] Download failed:', err);
      setError('Error al descargar');
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  }, [doc, loading]);

  if (!doc?.id) {
    return <span style={{ color: '#475569', fontSize: '0.78rem' }}>—</span>;
  }

  return (
    <button
      id={`b2b-grid-download-${doc.id}`}
      onClick={handleSingleDownload}
      disabled={loading}
      style={{
        ...S.downloadBtn,
        ...(loading ? S.downloadBtnLoading : {}),
      }}
      onMouseEnter={(e) => {
        if (!loading) {
          e.currentTarget.style.background = 'rgba(79, 70, 229, 0.35)';
          e.currentTarget.style.color = '#C7D2FE';
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'rgba(79, 70, 229, 0.18)';
        e.currentTarget.style.color = '#A5B4FC';
      }}
      title={`Descargar archivo ${doc.original_filename || ''}`}
    >
      {loading ? (
        <>
          <span
            style={{
              display: 'inline-block',
              width: '11px',
              height: '11px',
              border: '2px solid rgba(165,180,252,0.3)',
              borderTopColor: '#A5B4FC',
              borderRadius: '50%',
              animation: 'b2b-spin 0.7s linear infinite',
            }}
          />
          Descargando
        </>
      ) : error ? (
        <span style={{ color: '#F87171' }}>{error}</span>
      ) : (
        <>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          Descargar
        </>
      )}
    </button>
  );
};

// ─── Main component ────────────────────────────────────────────────────────────
const B2BDataGrid = ({ documents = [] }) => {
  const [savedMappings, setSavedMappings] = useState({});

  useEffect(() => {
    let isMounted = true;
    const fetchMappings = async () => {
      try {
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) return;
        const token = await getIdToken(user);

        const BACKEND_URL =
          process.env.REACT_APP_BACKEND_URL ||
          'https://backend-core-644060845776.us-central1.run.app';

        const response = await axios.get(`${BACKEND_URL}/api/b2b/vendor-mappings`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        
        if (isMounted && response.data) {
          setSavedMappings(response.data);
        }
      } catch (err) {
        console.error('[B2BDataGrid] Failed to fetch vendor mappings:', err);
      }
    };
    fetchMappings();
    return () => { isMounted = false; };
  }, []);

  // 1. FILTRADO BASE — State Machine Sync
  //    • ATTACHMENT → ghosted, excluded from ALL UI (neither cards nor table)
  //    • COMPLETED | MANUAL_REVIEW → rendered in the data grid
  //    • Everything else (PENDING | FAILED | REQUIRES_UPGRADE) → queued badge
  const { visibleDocs, queued } = useMemo(() => {
    const visibleDocs = [];  // COMPLETED + MANUAL_REVIEW
    const queued = [];       // non-terminal, non-ghost states
    for (const doc of documents) {
      const s = doc.extraction_status;
      if (s === 'ATTACHMENT') continue;  // ghost — strip entirely
      if (s === 'COMPLETED' || s === 'MANUAL_REVIEW') {
        visibleDocs.push(doc);
      } else {
        queued.push(doc); // PENDING | FAILED | REQUIRES_UPGRADE | …
      }
    }
    return { visibleDocs, queued };
  }, [documents]);

  // 2. PARSING — JSON.parse seguro con soporte para ambas nomenclaturas (snake_case y camelCase)
  const parsedDocs = useMemo(
    () =>
      visibleDocs.map((doc) => {
        let meta = {};
        try {
          const rawMeta = doc.extracted_metadata || doc.extractedMetadata;
          
          if (typeof rawMeta === 'string') {
            meta = JSON.parse(rawMeta || '{}');
          } else if (typeof rawMeta === 'object' && rawMeta !== null) {
            // Soporte en caso de que la respuesta ya venga parseada
            meta = rawMeta;
          }
        } catch {
          console.warn(`[B2BDataGrid] Invalid JSON metadata for doc ${doc.id}`);
        }
        return { ...doc, _meta: meta };
      }),
    [visibleDocs]
  );

  // 3. COLUMNAS DINÁMICAS — union de todas las keys presentes en metadata
  //    Exclude 'cuenta_contable' — rendered as a fixed, editable column below.
  const dynamicColumns = useMemo(() => {
    const keySet = new Set();
    for (const doc of parsedDocs) {
      if (doc._meta) {
        Object.keys(doc._meta)
          .filter((k) => k !== 'cuenta_contable')
          .forEach((k) => keySet.add(k));
      }
    }
    return Array.from(keySet);
  }, [parsedDocs]);

  // 4. CATEGORÍAS ÚNICAS para el dropdown
  const categories = useMemo(() => {
    const cats = new Set();
    for (const doc of parsedDocs) {
      if (doc._meta?.categoria) cats.add(doc._meta.categoria);
    }
    return Array.from(cats).sort();
  }, [parsedDocs]);

  // 5. Estado de filtros + cuenta-mapping local cache + toast
  const [search,        setSearch]        = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [hoveredRow,    setHoveredRow]    = useState(null);

  // Local cache: doc.id → account_code confirmed by the backend
  const [accountMapping, setAccountMapping] = useState(() => {
    const initial = {};
    for (const doc of parsedDocs) {
      if (doc._meta?.cuenta_contable) initial[doc.id] = doc._meta.cuenta_contable;
    }
    return initial;
  });

  // Toast state: null = hidden
  const [toast, setToast] = useState(null); // { message, type }

  const handleSaved = useCallback((docId, code) => {
    setAccountMapping((prev) => ({ ...prev, [docId]: code }));
    setToast({ message: '✓ Mapeo guardado', type: 'success' });
  }, []);

  const handleSaveError = useCallback((msg) => {
    setToast({ message: msg, type: 'error' });
  }, []);

  // 6. FILTRADO interactivo — busca en filename + todos los campos del metadata
  const filteredDocs = useMemo(() => {
    const query = search.trim().toLowerCase();
    return parsedDocs.filter((doc) => {
      if (categoryFilter && doc._meta?.categoria !== categoryFilter) return false;
      if (!query) return true;
      const haystack = [
        doc.original_filename ?? '',
        doc.billing_month ?? '',
        ...Object.values(doc._meta).map(String),
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [parsedDocs, search, categoryFilter]);

  if (!documents.length) return null;

  return (
    <>
      <style>{`
        @keyframes b2b-spin {
          to { transform: rotate(360deg); }
        }
        @keyframes b2b-toast-in {
          from { opacity: 0; transform: translateY(14px) scale(0.96); }
          to   { opacity: 1; transform: translateY(0)    scale(1); }
        }
        #b2b-grid-search:focus,
        #b2b-grid-category:focus {
          border-color: rgba(99, 102, 241, 0.7) !important;
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
        }
      `}</style>

      {/* Toast notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onDone={() => setToast(null)}
        />
      )}

      <div style={S.wrapper}>
        {/* Header */}
        <div style={S.header}>
          <h3 style={S.title}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6366F1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
              <polyline points="10 9 9 9 8 9" />
            </svg>
            Documentos Recibidos / Received Documents
          </h3>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            {queued.length > 0 && (
              <span style={S.queueBadge}>
                ⏳ {queued.length} {queued.length === 1 ? 'documento / document' : 'documentos / documents'} en cola / queued
              </span>
            )}
            <span style={S.countLabel}>
              {filteredDocs.length} / {visibleDocs.length} visibles / visible
            </span>
          </div>
        </div>

        {/* Barra de controles */}
        <div style={S.controlBar}>
          <input
            id="b2b-grid-search"
            style={S.searchInput}
            type="text"
            placeholder="🔍  Buscar / Search (archivo, emisor...)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Buscar documentos / Search documents"
          />
          {categories.length > 0 && (
            <select
              id="b2b-grid-category"
              style={S.categorySelect}
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              aria-label="Filtrar por categoría / Filter by category"
            >
              <option value="">Todas las categorías / All categories</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Tabla */}
        <div style={S.tableWrapper}>
          <table style={S.table} role="grid" aria-label="Tabla de documentos B2B / B2B Documents Table">
            <thead>
              <tr>
                <th style={S.th}>Archivo / File</th>
                {dynamicColumns.map((col) => (
                  <th key={col} style={S.th}>
                    {formatHeader(col)}
                  </th>
                ))}
                {visibleDocs.length > 0 && (
                  <th style={S.th} title="Código del Plan de Cuentas / Chart of Accounts Code">
                    Cuenta Contable
                  </th>
                )}
                <th style={S.th}>Mes / Month</th>
                <th style={S.thAction}>Acción / Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredDocs.length === 0 ? (
                <tr>
                  <td colSpan={dynamicColumns.length + 3} style={S.emptyState}>
                    {search || categoryFilter
                      ? 'Sin resultados para los filtros / No results found.'
                      : 'No hay documentos completados / No completed documents yet.'}
                  </td>
                </tr>
              ) : (
                filteredDocs.map((doc, i) => {
                  const isHovered = hoveredRow === doc.id;
                  const evenBg = 'rgba(255,255,255,0.022)';
                  const hoverBg = 'rgba(79,70,229,0.09)';
                  return (
                    <tr
                      key={doc.id}
                      onMouseEnter={() => setHoveredRow(doc.id)}
                      onMouseLeave={() => setHoveredRow(null)}
                      style={{
                        background: isHovered ? hoverBg : i % 2 === 0 ? evenBg : 'transparent',
                        transition: 'background 0.14s',
                      }}
                    >
                      {/* Filename — siempre primera */}
                      <td
                        style={{
                          ...S.td,
                          fontWeight: '500',
                          color: '#E2E8F0',
                          maxWidth: '220px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                        title={doc.original_filename}
                      >
                        {doc.original_filename}
                      </td>

                      {/* Columnas dinámicas del metadata */}
                      {dynamicColumns.map((col) => {
                        const formatted = formatCellValue(col, doc._meta[col]);
                        // Resaltar montos con acento verde
                        const isMonto = col === 'monto_total';
                        return (
                          <td
                            key={col}
                            style={{
                              ...S.td,
                              ...(isMonto
                                ? { color: '#34D399', fontFamily: 'monospace', fontWeight: '600' }
                                : {}),
                            }}
                          >
                            {formatted ?? (
                              <span style={{ color: '#334155' }}>—</span>
                            )}
                          </td>
                        );
                      })}

                      {/* Cuenta Contable — editable */}
                      {visibleDocs.length > 0 && (
                        <td style={S.td}>
                          <AccountCell
                            docId={doc.id}
                            emisor={doc._meta?.emisor || ''}
                            initialCode={savedMappings[(doc._meta?.emisor || '').trim().toLowerCase()] || accountMapping[doc.id] || doc._meta?.cuenta_contable || ''}
                            onSaved={handleSaved}
                            onError={handleSaveError}
                          />
                        </td>
                      )}

                      {/* Mes de cierre */}
                      <td
                        style={{
                          ...S.td,
                          color: '#818CF8',
                          fontFamily: 'monospace',
                          fontSize: '0.82rem',
                        }}
                      >
                        {doc.billing_month ?? (
                          <span style={{ color: '#334155' }}>—</span>
                        )}
                      </td>

                      {/* Acción — botón de descarga + MANUAL_REVIEW badge */}
                      <td style={S.tdAction}>
                        {doc.extraction_status === 'MANUAL_REVIEW' && (
                          <div>
                            <span
                              style={S.manualReviewBadge}
                              title="Validación matemática fallida — requiere revisión contable / Math validation failed — accounting review required"
                            >
                              ⚠ Revisión Manual
                            </span>
                          </div>
                        )}
                        <DownloadButton doc={doc} />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default B2BDataGrid;
